import { Flex } from 'components/Flex';
import { useTranslation } from 'hooks/useTranslation';
import {
  FlexWrapper,
  GeneralSettingsContentWrapper,
  SettingsRow,
  SettingsRowLabel,
  SettingsRowSubValue,
  SettingsRowValue,
  SettingsTable,
  SubHeader,
  EditButtonWrapper,
} from './GeneralSettingsPage.styled';
import { SettingsPageWrapper } from 'pages/Settings/SettingsPage.styled';
import { H3, P } from 'components/Typography';
import { FC } from 'react';
import { BKJButton } from '@bookjane2/bookjane-design-library';
import { useHistory } from 'react-router-dom';
import { TimeFormatEnum } from 'types/graphql';
import { useGeneralSettingsDataContext } from './GeneralSettingsPage.data.context';
import { useSelector } from 'react-redux';
import isUserSecondarySelector from 'store/selectors/userSelectors/isUserSecondarySelector';
import isUserEssentialSelector from 'store/selectors/userSelectors/isUserEssentialSelector';
import { useHasMarketplaceSelector } from 'store/selectors/organizationSelectors/orgSettingsSelector';
import { getNotificationPreferenceControlOptions } from './GeneralSettingsPage.constants';
import { HARDCODED_SHOW_SMS_EMAIL_NOTIFICATIONS_ON_GENERAL_SETTINGS_PAGE } from 'constants/hardcoded';

export const GeneralSettingsTabReadView: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { data } = useGeneralSettingsDataContext();
  const isUserSecondary = useSelector(isUserSecondarySelector);
  const isUserEssential = useSelector(isUserEssentialSelector);
  const hasMarketplace = useHasMarketplaceSelector();

  const organization = data?.fetchCommunity?.organization;

  const timeFormatLabel =
    organization?.timeFormatConfiguration === TimeFormatEnum.military
      ? t('settings:24_HOUR_TIME')
      : t('settings:12_HOUR_TIME');

  const enabledChannels: string[] = [];
  if (organization?.smsNotificationsEnabled) enabledChannels.push(t('settings:SMS'));
  if (organization?.emailShiftNotificationsEnabled) enabledChannels.push(t('settings:EMAIL'));
  const notificationChannelLabel = enabledChannels.length
    ? enabledChannels.join(', ')
    : t('settings:NONE');

  const notificationPreferenceControlLabel =
    getNotificationPreferenceControlOptions(t).find(
      (option) => option.value === organization?.notificationPreferenceControl,
    )?.label || '-';

  const handleEdit = () => {
    history.push('/settings/organization/general-settings/edit');
  };

  return (
    <GeneralSettingsContentWrapper>
      <Flex margin="0 0 40px 0">
        <FlexWrapper height="100%" width="100%">
          <SettingsPageWrapper>
            <Flex flexDirection="column" gap="10px">
              <Flex padding="0 0 30px 0" justifyContent="space-between" alignItems="center">
                <H3>{t('settings:GENERAL_SETTINGS')}</H3>
                {!isUserSecondary && (
                  <EditButtonWrapper>
                    <BKJButton variant="PurpleOutlined" onClick={handleEdit}>
                      {t('settings:EDIT')}
                    </BKJButton>
                  </EditButtonWrapper>
                )}
              </Flex>
              <Flex padding="0 0 40px 0">
                <P>{t('settings:GENERAL_SETTINGS_DESCRIPTION')}</P>
              </Flex>
              <Flex padding="0 0 15px 0">
                <SubHeader>{t('settings:GENERAL_SETTINGS')}</SubHeader>
              </Flex>
              <SettingsTable flexDirection="column" width="100%">
                <SettingsRow $grey alignItems="center" gap="40px">
                  <SettingsRowLabel flex="1">{t('settings:TIME_FORMAT')}</SettingsRowLabel>
                  <SettingsRowValue flex="1">{timeFormatLabel}</SettingsRowValue>
                </SettingsRow>
                {hasMarketplace && (
                  <SettingsRow alignItems="center" gap="40px">
                    <SettingsRowLabel flex="1">
                      {t('settings:AGENCY_LICENSE_NUMBER')}
                    </SettingsRowLabel>
                    <SettingsRowValue flex="1">
                      {organization?.licenceNumber || '-'}
                    </SettingsRowValue>
                  </SettingsRow>
                )}
                {!isUserEssential &&
                  HARDCODED_SHOW_SMS_EMAIL_NOTIFICATIONS_ON_GENERAL_SETTINGS_PAGE && (
                    <SettingsRow $grey={hasMarketplace} alignItems="center" gap="40px">
                      <SettingsRowLabel flex="1">
                        {t('settings:ALLOWED_SHIFT_AVAILABILITY_NOTIFICATION_CHANNEL')}
                      </SettingsRowLabel>
                      <SettingsRowValue flex="1">
                        <span>{notificationChannelLabel}</span>
                        <SettingsRowSubValue>
                          {`${t('settings:MANAGED_BY')} ${notificationPreferenceControlLabel}`}
                        </SettingsRowSubValue>
                      </SettingsRowValue>
                    </SettingsRow>
                  )}
              </SettingsTable>
            </Flex>
          </SettingsPageWrapper>
        </FlexWrapper>
      </Flex>
    </GeneralSettingsContentWrapper>
  );
};
